import type { DescuentosItem } from '@/shared/api/controllers/descuentos';
import type { ParametroComDescItem } from '@/shared/api/controllers/parametrosComDesc';
import { parseMoney } from '@/shared/lib/money';

/**
 * Pure discount-bracket engine — no SharePoint-client / browser deps (only
 * `parseMoney` + type-only imports), so it's unit-testable under tsx. See
 * `discounts.test.ts`. `discounts.ts` re-exports everything here and adds
 * the regalo-cap reader (which needs a value import from a controller).
 *
 * Three vendor discount brackets (A / B / C) live in
 * `99.ABM_ParametrosComDesc`, one param each:
 *   - `Valor_PCD` (TipoValor=Porcentaje) — the bracket's **percentage**,
 *     stored as a fraction (0,05 = 5%). Admin-configurable per country.
 *   - `ValorPorcentaje_PCD` (TipoValorAplicado=Cantidad) — the **count**
 *     allowed per month for that vendor (legacy naming quirk; it's a count).
 *
 * A line's discount needs approval when:
 *   (a) descuento doesn't match any configured bracket percentage
 *       ("Descuento Particular") — always requires approval, or
 *   (b) descuento matches a bracket AND the vendor has already hit that
 *       bracket's monthly count.
 */

export const DISCOUNT_BRACKETS = ['A', 'B', 'C'] as const;
export type DiscountBracket = (typeof DISCOUNT_BRACKETS)[number];
export type DiscountCategory = DiscountBracket | 'Otros' | 'Campana';

const TIPO_DESCUENTO_BY_BRACKET: Record<DiscountBracket, string> = {
  A: 'Descuento de Vendedores A',
  B: 'Descuento de Vendedores B',
  C: 'Descuento de Vendedores C',
};

/**
 * Legacy `"...Porc."` param/label names, matched alongside the new names
 * during the rename rollout so the engine is correct whether or not the
 * DB migration (rename params + historical `discounts` rows) has run yet.
 * Drop this once the migration is confirmed applied.
 */
const LEGACY_NAME_BY_BRACKET: Record<DiscountBracket, string> = {
  A: 'Descuentos de Vendedores 5 Porc.',
  B: 'Descuentos de Vendedores 10 Porc.',
  C: 'Descuentos de Vendedores 15 Porc.',
};

const TIPO_DESCUENTO_PARTICULAR = 'Descuento Particular';
const TIPO_DESCUENTO_CAMPANA = 'Campaña Activa';

/** Empty per-bracket counter. */
const emptyBracketRecord = (): Record<DiscountBracket, number> => ({ A: 0, B: 0, C: 0 });

/** Whether a stored param/label name is this bracket (new or legacy form). */
function nameIsBracket(name: string | null | undefined, b: DiscountBracket): boolean {
  const n = (name ?? '').trim();
  return n === TIPO_DESCUENTO_BY_BRACKET[b] || n === LEGACY_NAME_BY_BRACKET[b];
}

/**
 * The configured percentage for each bracket, read from `Valor_PCD`
 * (stored as a fraction → percent). `0` = bracket unconfigured (a
 * discount can never match it). This is what makes the percentages
 * admin-editable instead of the old hardcoded 5/10/15.
 */
export type BracketPercents = Record<DiscountBracket, number>;
export function readBracketPercents(params: ParametroComDescItem[]): BracketPercents {
  const pcts = emptyBracketRecord();
  for (const p of params) {
    const name = p.fields.Parametro_PCD;
    for (const b of DISCOUNT_BRACKETS) {
      if (nameIsBracket(name, b)) {
        const frac = parseMoney(p.fields.Valor_PCD) ?? 0;
        pcts[b] = Math.round(frac * 100 * 10_000) / 10_000;
      }
    }
  }
  return pcts;
}

/**
 * Categorize a discount against the configured bracket percentages.
 * Deterministic A→B→C order (first match wins if two brackets share a %).
 * Brackets with a 0 percentage are unconfigured and never match.
 */
export function categorizeDescuento(
  descuento: number,
  bracketPercents: BracketPercents,
): DiscountBracket | 'Otros' {
  for (const b of DISCOUNT_BRACKETS) {
    const pct = bracketPercents[b];
    if (pct > 0 && Math.abs(descuento - pct) < 0.01) return b;
  }
  return 'Otros';
}

export function tipoDescuentoLabel(
  descuento: number,
  bracketPercents: BracketPercents,
): string {
  const c = categorizeDescuento(descuento, bracketPercents);
  if (c === 'Otros') return TIPO_DESCUENTO_PARTICULAR;
  return TIPO_DESCUENTO_BY_BRACKET[c];
}

/**
 * Count Descuentos rows in the current month already attributed to the
 * vendor, bucketed by bracket. Only active/pending discounts count toward
 * the allowance. Matches new + legacy labels.
 *
 * Status note: a cancelled discount is written as `'Anulado'`, but on the
 * Supabase path the `abm_status` enum stores it as `Eliminado` and reads
 * back as `'Inactivo'` — so the old `=== 'Anulado'` check silently kept
 * cancelled discounts counted, and the monthly slot never freed after a
 * reject/anular. Count only `Activo` / `Pendiente Aprobacion`; skip
 * everything else (Anulado / Inactivo / Eliminado / empty).
 */
export function countMonthlyDescuentos(rows: DescuentosItem[]): Record<DiscountBracket, number> {
  const counts = emptyBracketRecord();
  for (const r of rows) {
    const status = (r.fields.Status_DESC ?? '').trim();
    if (status !== 'Activo' && status !== 'Pendiente Aprobacion') continue;
    const tipo = r.fields.TipoDescuento_DESC;
    for (const b of DISCOUNT_BRACKETS) {
      if (nameIsBracket(tipo, b)) {
        counts[b] += 1;
        break;
      }
    }
  }
  return counts;
}

/** Extract monthly count limits from the ParametrosComDesc list. */
export function readAllowanceLimits(
  params: ParametroComDescItem[],
): Record<DiscountBracket, number> {
  const limits = emptyBracketRecord();
  for (const p of params) {
    const name = p.fields.Parametro_PCD;
    for (const b of DISCOUNT_BRACKETS) {
      if (nameIsBracket(name, b)) {
        limits[b] = parseMoney(p.fields.ValorPorcentaje_PCD) ?? 0;
      }
    }
  }
  return limits;
}

export type AllowanceCheck = {
  descuento: number;
  category: DiscountCategory;
  tipoLabel: string;
  /** True when the line needs to be flagged `Pendiente Aprobacion`. */
  needsAuthorization: boolean;
  /** Short message to show the user — why auth was triggered. */
  motivo: string;
};

/**
 * Decide whether a single line's discount needs authorization, given
 * the vendor's monthly history and the parameter limits.
 *
 * `extraUsed` lets the caller account for other in-flight lines on the
 * same cotización that will also consume the allowance — pass the
 * count of *already-added* lines at each bracket so a single submit
 * with two same-bracket lines correctly flags the second one.
 */
export function evaluateLineDiscount({
  descuento,
  esRegalo = false,
  campaignPct = null,
  bracketPercents,
  monthlyCounts,
  limits,
  extraUsed = emptyBracketRecord(),
}: {
  descuento: number;
  esRegalo?: boolean;
  /** Active campaign pct for this article, or null if none. */
  campaignPct?: number | null;
  /** Configured percentage per bracket (admin-editable). */
  bracketPercents: BracketPercents;
  monthlyCounts: Record<DiscountBracket, number>;
  limits: Record<DiscountBracket, number>;
  extraUsed?: Record<DiscountBracket, number>;
}): AllowanceCheck {
  const category = categorizeDescuento(descuento, bracketPercents);
  const tipoLabel = tipoDescuentoLabel(descuento, bracketPercents);

  if (esRegalo) {
    return { descuento, category, tipoLabel, needsAuthorization: false, motivo: '' };
  }

  if (descuento <= 0) {
    return { descuento, category, tipoLabel, needsAuthorization: false, motivo: '' };
  }

  // Campaign match: the exact campaign pct is always free of authorization
  // and doesn't consume the vendor's monthly allowance (mirrors PowerApps,
  // where articles in Collect_CampaniasActivas are excluded from the
  // bracket-counter check).
  if (campaignPct != null && descuento === campaignPct) {
    return {
      descuento,
      category: 'Campana',
      tipoLabel: TIPO_DESCUENTO_CAMPANA,
      needsAuthorization: false,
      motivo: '',
    };
  }

  // Article has a campaign but the vendor chose a different pct — PowerApps
  // treats any non-campaign discount on a campaign article as Descuento
  // Particular (needs a written motivo), regardless of the value.
  if (campaignPct != null) {
    return {
      descuento,
      category: 'Otros',
      tipoLabel: TIPO_DESCUENTO_PARTICULAR,
      needsAuthorization: true,
      motivo: `Descuento distinto al de la Campaña Activa (${campaignPct}%)`,
    };
  }

  if (category === 'Otros') {
    return {
      descuento,
      category,
      tipoLabel,
      needsAuthorization: true,
      motivo: 'Descuento Particular requiere aprobación',
    };
  }

  // category is A/B/C here — 'Otros'/'Campana' already returned above.
  const bracket = category as DiscountBracket;
  const used = monthlyCounts[bracket] + extraUsed[bracket];
  const limit = limits[bracket];

  if (limit > 0 && used >= limit) {
    return {
      descuento,
      category,
      tipoLabel,
      needsAuthorization: true,
      motivo: 'Límite Mensual excedido',
    };
  }

  return { descuento, category, tipoLabel, needsAuthorization: false, motivo: '' };
}
